import {Time} from 'yougile-util'
import {
  ChatMessage,
  DataObjectId,
  getNearestMention,
  Hub,
  NotificationSettings,
  sharingGuestAccount
} from 'yougile-model'
import {getCountMentions} from 'yougile-model'
import {SharedBoardsClient} from '@client/shared-boards-client'
import {sharedBoardsEvents} from '@client/shared-boards-events'
import {Sync} from '@client/sync'
import {NotificationSettingsService} from '@extensions/notifications-settings-service'
import {globals} from './globals-base'

export interface UnseenMessagesProps {
  userId: DataObjectId
  hub: Hub
  startFrom?: number
  includeSystem?: boolean
  forceStartFrom?: boolean
  notificationSettings?: NotificationSettings
}

export const getUnseenMessages = (props: UnseenMessagesProps): number => {
  const {userId, hub, startFrom = 0, notificationSettings} = props
  const {includeSystem = false, forceStartFrom = false} = props

  const filter =
    notificationSettings &&
    NotificationSettingsService.getTaskMessageFilter(notificationSettings, hub)

  if (!SharedBoardsClient.getId() && globals.currentUserId) {
    return hub.chat.newMessagesFor(
      globals.currentUserId,
      startFrom,
      includeSystem,
      forceStartFrom,
      filter
    )
  } else {
    if (userId !== globals.currentUserId && userId !== sharingGuestAccount.id) {
      return 0
    }
    /* startFrom is ignored here, timestamp is used instead
       (in `getLastSeenMessageTime`) */
    return hub.chat.newMessagesFor(
      sharingGuestAccount.id,
      getLastSeenMessageTime(hub),
      undefined,
      forceStartFrom,
      filter
    )
  }
}

interface GetUnseenMentionsProps {
  userId: DataObjectId
  hub: Hub
  startFrom?: number
}

export const getUnseenMentions = (props: GetUnseenMentionsProps): number => {
  const {userId, hub, startFrom} = props

  if (!SharedBoardsClient.isGuest() && globals.currentUserId) {
    return newMentionsFor(globals.currentUserId, startFrom, hub.id)
  } else {
    if (userId !== globals.currentUserId && userId !== sharingGuestAccount.id) {
      return 0
    }

    /* startFrom is ignored here, timestamp is used instead
       (in `getLastSeenMessageTime`) */
    return newMentionsFor(
      sharingGuestAccount.id,
      getLastSeenMessageTime(hub),
      hub.id
    )
  }
}

export const getLastSeenMessageTime = (hub: Hub): number => {
  if (!SharedBoardsClient.getId() && globals.currentUserId) {
    return hub.chat.getLastSeenMessage(globals.currentUserId)
  } else {
    const readTime =
      SharedBoardsClient.guest?.available?.[hub.companyId]?.read?.[hub.id] ?? 0
    const timestamp = SharedBoardsClient.guest?.timestamp ?? 0
    return Math.max(readTime, timestamp)
  }
}

export const readChatMessages = async (hub: Hub): Promise<void> => {
  if (!SharedBoardsClient.getId() && globals.currentUserId) {
    hub.chat.read(globals.currentUserId)
    hub.commit()
  } else {
    if (getUnseenMessages({userId: sharingGuestAccount.id, hub}) > 0) {
      SharedBoardsClient.guest.available[hub.companyId].read[hub.id] =
        Time.now()
      await SharedBoardsClient.changeGuest({readChatId: hub.id})
      await sharedBoardsEvents.trigger()
    }
  }
}

export const newMentionsFor = (
  userId: DataObjectId,
  startFrom: number | undefined,
  hubId: DataObjectId
): number => {
  if (!userId) {
    return 0
  }

  const hub = Sync.getCurrentSandbox().getHub(hubId)
  if (!hub) {
    return 0
  }

  startFrom ??= getLastSeenMessageTime(hub)
  const startTime = Math.max(startFrom, Time.now() - 100 * 24 * 3600_000)
  return getCountMentions(hub.chat.messages, userId, startTime)
}

export const getLastMentionMessage = (
  hub: Hub,
  userId: DataObjectId,
  timeFrom: number
): ChatMessage | undefined => {
  const chat = hub.chat
  if (chat.messages.size === 0) {
    return undefined
  }

  return getNearestMention(chat.messages, userId, timeFrom)
}
